import { useState } from 'react';
import {
  PolicyFaction,
  type GameView,
  type PolicyType,
  type PrivatePlayerState,
  type PublicGameState,
  type SpeechClaimInput,
} from '@secretnenshi/shared';
import { HoldToReveal } from '../components/HoldToReveal';
import { Button, Narrator } from '../components/ui';
import { play } from '../state/actions';
import { formatClock, nameOf, type Copy } from './copy';
import { useCountdown } from './useCountdown';

/*
 * End-of-round phases. Everything shown here is public, except the hold-to-
 * view recall, which only the Minister and Premier have.
 */

interface Props {
  view: GameView;
  game: PublicGameState;
  me: string;
  priv: PrivatePlayerState | null;
  busy: boolean;
  isHost: boolean;
  c: Copy;
}

const F = PolicyFaction.FEDERALIST;
const S = PolicyFaction.SEPARATION;

function Clock({ label }: { label: string }) {
  const ms = useCountdown();
  if (ms === null) return null;
  return (
    <p className="clock" role="timer" aria-label={`${label}: ${formatClock(ms)} left`}>
      <span className="clock-label">{label}</span>
      <span className={`clock-time ${ms < 15_000 ? 'clock-low' : ''}`}>{formatClock(ms)}</span>
    </p>
  );
}

function TruthCaution() {
  return (
    <p className="caution">
      <strong>Nobody is required to tell the truth.</strong> Claims are whatever the speaker says they are.
    </p>
  );
}

/** Hold to see what you really received and discarded this round. */
function Recall({ priv, c }: { priv: PrivatePlayerState | null; c: Copy }) {
  const r = priv?.legislativeRecall;
  if (!r) return null;
  return (
    <HoldToReveal label="What you actually saw" hint="Hold to review">
      {() => (
        <>
          <p className="reveal-label">You received</p>
          {r.received.map((p, i) => (
            <p key={i}>{c.policy(p)}</p>
          ))}
          <div className="reveal-block">
            <p className="reveal-label">{r.role === 'PREMIER' && r.discarded.length === 2 ? 'You vetoed' : 'You discarded'}</p>
            {r.discarded.map((p, i) => (
              <p key={i}>{c.policy(p)}</p>
            ))}
          </div>
        </>
      )}
    </HoldToReveal>
  );
}

// ---------------------------------------------------------------------------
// Speech from the Throne
// ---------------------------------------------------------------------------

export function SpeechFromThrone({ view, game, me, priv, busy, isHost, c }: Props) {
  const sp = game.speech!;
  const role = me === sp.ministerPlayerId ? 'MINISTER' : me === sp.premierPlayerId ? 'PREMIER' : null;
  const spoken = role === 'MINISTER' ? sp.ministerSubmitted : role === 'PREMIER' ? sp.premierSubmitted : true;

  return (
    <section className="action" aria-live="polite">
      <h2 className="action-title">{c.t.phases.speech.name}</h2>
      <Narrator>{c.t.phases.speech.narrator}</Narrator>
      <p>
        {c.minister} <strong>{nameOf(view, sp.ministerPlayerId)}</strong> and {c.premier} <strong>{nameOf(view, sp.premierPlayerId)}</strong> now
        tell the House what they received and what they discarded
        {sp.vetoed ? ' before the veto' : ''}. They can explain out loud too.
      </p>
      <TruthCaution />
      <p className="hint-left">Both speeches stay sealed until both have spoken, then everyone sees them together.</p>
      <Clock label="Time to speak" />

      <ul className="speakers">
        <li>
          <span>
            {c.minister} {nameOf(view, sp.ministerPlayerId)}
          </span>
          <span>{sp.ministerSubmitted ? 'Has spoken' : 'Preparing remarks…'}</span>
        </li>
        <li>
          <span>
            {c.premier} {nameOf(view, sp.premierPlayerId)}
          </span>
          <span>{sp.premierSubmitted ? 'Has spoken' : 'Preparing remarks…'}</span>
        </li>
      </ul>

      {role && !spoken ? (
        <>
          <Recall priv={priv} c={c} />
          {role === 'MINISTER' ? <MinisterSpeech busy={busy} c={c} /> : <PremierSpeech busy={busy} vetoed={sp.vetoed} enacted={sp.enacted} c={c} />}
        </>
      ) : null}
      {role && spoken ? (
        <>
          <p className="vote-recorded">
            Your speech: {priv?.ownSpeech ? c.describeClaim(priv.ownSpeech) : 'delivered'} It stays sealed until the other speaker finishes.
          </p>
          <Recall priv={priv} c={c} />
        </>
      ) : null}

      {isHost ? (
        <Button variant="quiet" disabled={busy} onClick={() => void play.skipRoundPhase()}>
          Move on now (host)
        </Button>
      ) : null}
    </section>
  );
}

function Choice<T extends string | number>({
  options,
  value,
  onChange,
  label,
}: {
  options: Array<{ value: T; label: string; disabled?: boolean }>;
  value: T | null;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div className="choices choices-stack" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          disabled={o.disabled}
          className={`choice ${value === o.value ? 'selected' : ''}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Hand descriptions for n Federalist-slot cards out of `size`. */
function handOptions(c: Copy, size: number) {
  const fed = c.t.policies.FEDERALIST.short;
  const sep = c.t.policies.SEPARATION.short;
  return Array.from({ length: size + 1 }, (_, i) => size - i).map((n) => ({
    value: n,
    label: [n ? `${n} ${fed}` : null, size - n ? `${size - n} ${sep}` : null].filter(Boolean).join(', '),
  }));
}

function MinisterSpeech({ busy, c }: { busy: boolean; c: Copy }) {
  const [fed, setFed] = useState<number | null>(null);
  const [discarded, setDiscarded] = useState<PolicyType | null>(null);
  const valid = fed !== null && discarded !== null && (discarded === F ? fed > 0 : fed < 3);
  const pickHand = (n: number) => {
    setFed(n);
    if (discarded === F && n === 0) setDiscarded(null);
    if (discarded === S && n === 3) setDiscarded(null);
  };
  return (
    <div className="speech-form">
      <p className="field-label">I received</p>
      <Choice label="Policies received" options={handOptions(c, 3)} value={fed} onChange={pickHand} />
      <p className="field-label">and discarded</p>
      <Choice
        label="Policy discarded"
        options={[
          { value: F, label: `A ${c.policy(F)}`, disabled: fed === 0 },
          { value: S, label: `A ${c.policy(S)}`, disabled: fed === 3 },
        ]}
        value={discarded}
        onChange={setDiscarded}
      />
      <Button
        disabled={!valid || busy}
        onClick={() => void play.giveSpeech({ received: { FEDERALIST: fed!, SEPARATION: 3 - fed! }, discarded: discarded! } as SpeechClaimInput)}
      >
        Deliver speech
      </Button>
      <Button variant="secondary" disabled={busy} onClick={() => void play.giveSpeech({ noComment: true })}>
        No comment
      </Button>
    </div>
  );
}

function PremierSpeech({ busy, vetoed, enacted, c }: { busy: boolean; vetoed: boolean; enacted?: PolicyType; c: Copy }) {
  const [fed, setFed] = useState<number | null>(null);
  const options = handOptions(c, 2).map((o) => ({
    ...o,
    // What was enacted is public, so it must be one of the two claimed.
    disabled: !vetoed && ((enacted === F && o.value === 0) || (enacted === S && o.value === 2)),
  }));
  const other = fed === null || vetoed || !enacted ? null : fed === 1 ? (enacted === F ? S : F) : enacted;
  return (
    <div className="speech-form">
      <p className="field-label">The {c.minister} sent me</p>
      <Choice label="Policies received" options={options} value={fed} onChange={setFed} />
      {other ? (
        <p className="hint-left">
          You enacted a {c.policy(enacted!)}, so you’ll be saying you discarded a {c.policy(other)}.
        </p>
      ) : null}
      {vetoed ? <p className="hint-left">The session was vetoed, so both were discarded.</p> : null}
      <Button disabled={fed === null || busy} onClick={() => void play.giveSpeech({ received: { FEDERALIST: fed!, SEPARATION: 2 - fed! } })}>
        Deliver speech
      </Button>
      <Button variant="secondary" disabled={busy} onClick={() => void play.giveSpeech({ noComment: true })}>
        No comment
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Question Period
// ---------------------------------------------------------------------------

export function QuestionPeriod({ view, game, me, priv, busy, isHost, c }: Props) {
  const qp = game.questionPeriod!;
  const self = game.players.find((p) => p.id === me);
  const voted = qp.endVotePlayerIds.includes(me);
  const turn = game.log.filter((l) => l.turn === game.turnNumber);
  const vote = turn.find((l) => l.type === 'VOTE_RESOLVED');
  const enacted = turn.filter((l) => l.type === 'POLICY_ENACTED');
  const vetoed = turn.some((l) => l.type === 'VETO_RESOLVED' && l.accepted);
  const sp = game.speech;

  return (
    <section className="action" aria-live="polite">
      <h2 className="action-title">{c.t.phases.questionPeriod.name}</h2>
      <Clock label="Debate ends in" />
      <Narrator>{c.t.phases.questionPeriod.narrator}</Narrator>
      <p>
        Open debate. Question the {c.minister} and {c.premier}, compare their speeches, accuse, defend, make alliances. When a majority votes
        to end debate, or time runs out, the next {c.minister} takes office.
      </p>
      <TruthCaution />

      <div className="recap">
        <p className="field-label">This round</p>
        <ul className="plain">
          {vote && vote.type === 'VOTE_RESOLVED' ? (
            <li>
              {nameOf(view, game.publicVoteResult?.premierPlayerId)} for {c.premier}:{' '}
              {vote.passed ? 'approved' : 'defeated'} {vote.confidence}–{vote.noConfidence}.
            </li>
          ) : null}
          {vetoed ? <li>The session was vetoed.</li> : null}
          {enacted.map((l, i) =>
            l.type === 'POLICY_ENACTED' ? (
              <li key={i}>
                {c.policy(l.policy)} enacted{l.source === 'GRIDLOCK' ? ` automatically (${c.t.phases.gridlock.short})` : ''}.
              </li>
            ) : null,
          )}
          {sp?.ministerClaim ? (
            <li>
              {c.minister} {nameOf(view, sp.ministerPlayerId)}: {c.describeClaim(sp.ministerClaim)}
            </li>
          ) : null}
          {sp?.premierClaim ? (
            <li>
              {c.premier} {nameOf(view, sp.premierPlayerId)}: {c.describeClaim(sp.premierClaim)}
            </li>
          ) : null}
        </ul>
      </div>

      <Recall priv={priv} c={c} />

      {self?.active ? (
        <Button variant={voted ? 'secondary' : 'primary'} disabled={busy} onClick={() => void play.voteEndDebate(!voted)}>
          {voted ? 'Withdraw vote to end debate' : 'Vote to end debate'}
        </Button>
      ) : (
        <p className="hint-left">Removed players can listen but not vote.</p>
      )}
      <p className="waiting">
        {qp.endVotePlayerIds.length} of {qp.votesNeeded} votes needed to end debate.
      </p>
      {isHost ? (
        <Button variant="quiet" disabled={busy} onClick={() => void play.skipRoundPhase()}>
          End {c.t.phases.questionPeriod.name} now (host)
        </Button>
      ) : null}
    </section>
  );
}
